import { Directive } from '@angular/core';
import { Observable } from 'rxjs';

import { ApiResponseModel } from '../../models/core/api-response.model';
import { PostValueResponseModel } from '../../models/core/post-value-response.model';
import { BaseEntityModel } from './base-entity.model';
import { BaseSearchResource } from './base-search.resource';

/**
 * Resource base class for CRUD.
 */
@Directive()
export abstract class BaseEntityResource<
    T extends BaseEntityModel,
    TSummary extends BaseEntityModel = T,
    TList extends BaseEntityModel = T
> extends BaseSearchResource<TList> {
    /**
     * Retrieves an item.
     * @param id Item's ID.
     */
    get(id: string): Observable<ApiResponseModel<T>> {
        return this._requestService.makeGet<ApiResponseModel<T>>(
            this.getUrl(this._config.actions.get),
            id,
            undefined,
            this._isAbsoluteUrl,
            true
        );
    }

    /**
     * Retrieves the summary of an item.
     * @param id Item's ID.
     */
    getSummary(id: string): Observable<ApiResponseModel<TSummary>> {
        return this._requestService.makeGet<ApiResponseModel<TSummary>>(
            this.getUrl(`${id}/summary`),
            undefined,
            undefined,
            this._isAbsoluteUrl,
            true
        );
    }

    /**
     * Creates a new item.
     * @param data Item's data.
     */
    add(data: T): Observable<ApiResponseModel<PostValueResponseModel>> {
        return this._requestService.makePost<ApiResponseModel<PostValueResponseModel>>(
            this.getUrl(this._config.actions.add),
            data,
            undefined,
            this._isAbsoluteUrl,
            undefined,
            true
        );
    }

    /**
     * Update an item's data.
     * @param data Data to be updated.
     */
    edit(data: T): Observable<ApiResponseModel<PostValueResponseModel>> {
        return this._requestService.makePut<ApiResponseModel<PostValueResponseModel>>(
            `${this.getUrl(this._config.actions.edit)}/${data.id}`,
            data,
            undefined,
            this._isAbsoluteUrl,
            true
        );
    }

    /**
     * Removes the informed item.
     * @param id Item's ID to be removed.
     */
    remove(id: string): Observable<ApiResponseModel<boolean>> {
        return this._requestService.makeDelete<ApiResponseModel<boolean>>(
            this.getUrl(this._config.actions.remove),
            id,
            undefined,
            this._isAbsoluteUrl
        );
    }
}
